import Tippy from '@tippyjs/react'
import IMask, { type InputMask as Mask } from 'imask'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { classNameParser } from '../../../../helpers'
import type { InputProps } from '../../../../types'

import '../../../../styles/input.css'
import './style.css'

import iconHelp from '../../../../images/ic-help.svg'
import iconInfo from '../../../../images/ic-info.svg'
import iconLock from '../../../../images/icash-lock.svg'
import iconRocket from '../../../../images/icash-rocket.svg'

type SinInputProps = Omit<
  InputProps<
    string,
    {
      postTooltip?: ReactNode
      preTooltip?: ReactNode
      hardFillValue?: string
      hideTime?: number
      postIcon?: 'rocket' | 'help' | 'info'
      preIcon?: 'lock'
      clearOnFocus?: boolean
    }
  >,
  'type' | 'inputMode'
>

function toMask(value: string) {
  const formattedValue = value.replace(/\s/g, '').trim()
  const hiddenLength = formattedValue.length > 0 ? formattedValue.length - 1 : 9
  let i = 1
  let mask = ''

  while (i <= 9) {
    if (i <= hiddenLength) {
      mask += 's'
    } else {
      mask += '0'
    }

    i++
  }

  return mask.replace(/(.{3})(.{3})(.{3})/, '$1 $2 $3')
}

const ICONS: {
  [key: string]: {
    icon: { src: string }
    width?: number
    height?: number
    class?: string
  }
} = {
  rocket: {
    icon: iconRocket,
    width: 32,
    height: 32,
    class: 'mr-4',
  },
  help: {
    icon: iconHelp,
  },
  info: {
    icon: iconInfo,
  },
}

const ARROW_CODES = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']

export const SinInputV2 = (props: SinInputProps) => {
  const { onChange } = props
  const [postInfoTarget, setPostInfoTarget] = useState<HTMLElement>()
  const [preInfoTarget, setPreInfoTarget] = useState<HTMLElement>()
  const [fieldTarget, setFieldTarget] = useState<HTMLElement>()
  const [value, setValue] = useState<string>(props.value || '')
  const [updatedValue, setUpdatedValue] = useState<string>('')

  const inputRef = useRef<HTMLInputElement>(null)
  const masked = useRef<Mask<any>>(undefined)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.clearOnFocus && masked.current) {
      setValue('')
      masked.current.value = ''
      masked.current.unmaskedValue = ''
      masked.current.rawInputValue = ''
    }
    if (props.onFocus) props.onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur) props.onBlur(e)
  }

  useEffect(() => {
    if (props.hardFillValue !== undefined) {
      setValue(props.hardFillValue)
    }
  }, [props.hardFillValue])

  useEffect(() => {
    if (!inputRef?.current) return

    masked.current = IMask(inputRef.current, {
      mask: 'sss sss sss',
      blocks: {
        s: {
          mask: '0',
          displayChar: '•',
        },
      },
      placeholderChar: '',
    })

    const maskInput = masked.current
    maskInput.value = value
    setUpdatedValue(maskInput.unmaskedValue)

    function onAccept() {
      if (timer.current) clearTimeout(timer.current)

      const curState = maskInput.unmaskedValue.length
      const prevState = maskInput.history.currentState?.unmaskedValue.length || 0
      const isAddChar = curState > prevState

      setUpdatedValue(maskInput.unmaskedValue)

      maskInput.updateOptions({
        mask: isAddChar ? toMask(maskInput.value) : 'sss sss sss',
      })

      timer.current = setTimeout(() => {
        maskInput.updateOptions({
          mask: 'sss sss sss',
        })
      }, props.hideTime || 1000)
    }

    maskInput.on('accept', onAccept)

    return () => {
      maskInput.off('accept', onAccept)
      masked.current?.destroy()
    }
  }, [props.hideTime, value])

  useEffect(() => {
    const maskInput = masked.current
    if (!maskInput) return

    const handleValueChange = () => {
      if (onChange) onChange(masked.current?.unmaskedValue)
    }

    maskInput.on('accept', handleValueChange)
    return () => {
      maskInput.off('accept', handleValueChange)
    }
  }, [masked.current, onChange])

  return (
    <>
      <Tippy
        reference={fieldTarget}
        triggerTarget={preInfoTarget}
        disabled={!props.preTooltip}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.preTooltip}
          </div>
        }
      />
      <Tippy
        reference={fieldTarget}
        triggerTarget={postInfoTarget}
        disabled={!props.postTooltip}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.postTooltip}
          </div>
        }
      />
      <div
        ref={(el) => {
          if (el) setFieldTarget(el)
        }}
        className={classNameParser('global--input-container input--text-input-container', props.className)}
        id={props.id}
        key={props.key}
        style={props.style}
      >
        <div className="absolute right-0 top-[-20px] flex items-center">
          {props.optional === true ? (
            <p className={`global--input-label font-400 !text-[12px]`}>Optional field</p>
          ) : null}
        </div>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            className={`global--input ${updatedValue ? 'global--with-value' : ''} ${
              props.postTooltip !== undefined ? '!pr-[35px]' : ''
            } ${props.preTooltip !== undefined ? '!pl-[48px]' : ''} ${!props.autoComplete ? 'hide-autocomplete' : ''}`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(event) => {
              if (ARROW_CODES.includes(event.code)) {
                event.preventDefault()
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault()
              const input = inputRef.current

              if (!input) return

              input.focus()
              queueMicrotask(() => {
                input.setSelectionRange(input.value.length, input.value.length)
              })
            }}
            // biome-ignore lint/a11y/noAutofocus: opt-in through the autoFocus prop
            autoFocus={props.autoFocus}
            disabled={props.disabled}
            placeholder={props.placeHolder}
            {...(props.minLength ? { minLength: props.minLength } : {})}
            {...(props.maxLength ? { maxLength: props.maxLength } : {})}
          />
          {props.preTooltip !== undefined ? (
            <div
              ref={(el) => {
                if (el) setPreInfoTarget(el)
              }}
              className="absolute left-0 top-0 h-full flex items-center pl-4"
            >
              <img src={iconLock.src} alt="lock icon" width={24} height={24}></img>
            </div>
          ) : null}
          {props.postTooltip !== undefined ? (
            <div
              ref={(el) => {
                if (el) setPostInfoTarget(el)
              }}
              className="absolute right-0 top-0 flex items-center h-full"
            >
              {props.postIcon && ICONS[props.postIcon] ? (
                <img
                  className={`${ICONS[props.postIcon]?.class || ''}`}
                  src={ICONS[props.postIcon]?.icon.src}
                  alt={`${props.postIcon} icon`}
                  width={ICONS[props.postIcon]?.width}
                  height={ICONS[props.postIcon]?.height}
                ></img>
              ) : null}
            </div>
          ) : null}
          {props.label && (
            <label
              htmlFor={props.id}
              className={`input--text-input-label ${props.preTooltip ? 'has-pre-icon' : ''} ${props.postTooltip ? 'has-tooltip' : ''}`}
              title={props.label}
            >
              <span className="input--text-input-label-text">{props.label}</span>
            </label>
          )}
        </div>
        {props.error && <p className="global--input-error">{props.error}</p>}
      </div>
    </>
  )
}
